import React, { lazy, Suspense, useMemo } from 'react';
import {
  LuArrowRight,
  LuCircleCheck,
  LuCircleX,
  LuClock,
  LuGauge,
  LuLayers,
  LuPlay,
  LuServer,
  LuTrendingDown,
  LuTriangleAlert,
} from 'react-icons/lu';
import { SearchInput } from '../ui';
import {
  useCardData,
  useCaseOverview,
  useDebouncedUrlSearch,
  useEnvironmentReport,
  useExecutionHistory,
  useExecutionSummary,
  useSuiteReport,
} from './reportHooks';
import {
  formatDate,
  formatDuration,
  formatPercent,
  formatSigned,
  passRateTone,
  toNumber,
} from './reportData';
import {
  HealthPill,
  KpiStrip,
  OutcomeBar,
  PassRateBar,
  ReportEmptyState,
  ReportErrorState,
  ReportKpi,
  ReportSection,
  ReportSkeleton,
  ReportTable,
} from './ReportUI';

const TrendChart = lazy(() => import('./TrendChart'));

const ATTENTION_ICONS = {
  danger: LuCircleX,
  warning: LuTriangleAlert,
  info: LuClock,
  success: LuCircleCheck,
};

function buildAttentionItems({ card, envs, caseOverview, execSummary, nav }) {
  const items = [];
  if (!card) return items;

  if (card.failed > 0) {
    items.push({
      key: 'failed',
      tone: 'danger',
      title: `${card.failed.toLocaleString()} failed test execution${card.failed !== 1 ? 's' : ''}`,
      detail: 'See which tests fail repeatedly and where failures cluster.',
      action: { label: 'Open failures', onClick: () => nav.toReport('failures') },
    });
  }

  const failingSuites = (caseOverview?.chart || [])
    .filter((s) => toNumber(s.failed) > 0)
    .sort((a, b) => toNumber(b.failed) - toNumber(a.failed));
  if (failingSuites.length) {
    items.push({
      key: 'suites',
      tone: 'danger',
      title: `${failingSuites.length} suite${failingSuites.length !== 1 ? 's' : ''} with failing tests`,
      detail: failingSuites
        .slice(0, 3)
        .map((s) => `${s.suite_name || '—'} (${s.failed})`)
        .join(' · '),
      action: {
        label: 'Failed cases',
        onClick: () => nav.toReport('detailed', { view: 'cases', c_status: 'Failed' }),
      },
    });
  }

  const topPlan = execSummary?.topFailedPlans?.[0];
  if (topPlan && toNumber(topPlan.failed_test_cases) > 0) {
    const others = execSummary.topFailedPlans.length - 1;
    items.push({
      key: 'plan',
      tone: 'warning',
      title: `“${topPlan.test_plan_name || 'Test plan'}” has ${topPlan.failed_test_cases} failed case${
        topPlan.failed_test_cases !== 1 ? 's' : ''
      }`,
      detail: others > 0 ? `${others} more test plan${others !== 1 ? 's' : ''} with failures` : 'Most failures by test plan',
      action: {
        label: 'Plan runs',
        onClick: () => nav.toReport('detailed', { view: 'plans', p_q: topPlan.test_plan_name || '' }),
      },
    });
  }

  const weakEnvs = (envs || []).filter((e) => e.totalExecuted > 0 && e.passRate < 70);
  if (weakEnvs.length) {
    items.push({
      key: 'envs',
      tone: 'warning',
      title: `${weakEnvs.length} environment${weakEnvs.length !== 1 ? 's' : ''} below 70% pass rate`,
      detail: weakEnvs
        .slice(0, 3)
        .map((e) => `${e.name} (${formatPercent(e.passRate)})`)
        .join(' · '),
      action: { label: 'Environments', onClick: () => nav.toReport('detailed', { view: 'environments' }) },
    });
  }

  const ta = card.trendAnalysis;
  if (ta && toNumber(ta.passRateTrend) < 0) {
    items.push({
      key: 'trend',
      tone: 'warning',
      title: `Pass rate down ${Math.abs(toNumber(ta.passRateTrend)).toFixed(1)}% vs previous period`,
      detail: ta.comparisonPeriod?.from
        ? `Compared with ${formatDate(ta.comparisonPeriod.from)} – ${formatDate(ta.comparisonPeriod.to)}`
        : 'Compared with the previous period of the same length',
    });
  }

  if (card.flakiness != null && toNumber(card.flakiness) > 0) {
    items.push({
      key: 'flaky',
      tone: 'warning',
      title: `Flakiness index ${formatPercent(card.flakiness, 1)}`,
      detail: 'Some tests switch between pass and fail across runs.',
      action: { label: 'Recurring failures', onClick: () => nav.toReport('failures') },
    });
  }

  if (ta && toNumber(ta.executionTimeTrend) > 0) {
    items.push({
      key: 'slower',
      tone: 'info',
      title: `Executions took ${formatDuration(toNumber(ta.executionTimeTrend))} longer`,
      detail: 'Total execution time increased vs previous period.',
      action: { label: 'Slowest tests', onClick: () => nav.toReport('detailed') },
    });
  }

  if (card.notExecuted > 0) {
    items.push({
      key: 'notrun',
      tone: 'info',
      title: `${card.notExecuted.toLocaleString()} test case${card.notExecuted !== 1 ? 's' : ''} not executed`,
      detail: 'No run recorded in this period — a coverage gap.',
    });
  }

  return items;
}

function AttentionList({ items }) {
  if (!items.length) {
    return (
      <ReportEmptyState
        compact
        icon={<LuCircleCheck size={20} />}
        title="Nothing needs attention"
        description="No failures, weak environments or regressions in this period."
      />
    );
  }
  return (
    <ul className="rpt-attention">
      {items.slice(0, 6).map((item) => {
        const Icon = ATTENTION_ICONS[item.tone] || LuTriangleAlert;
        return (
          <li key={item.key} className={`rpt-attention__item rpt-attention__item--${item.tone}`}>
            <span className="rpt-attention__icon" aria-hidden>
              <Icon size={16} />
            </span>
            <span className="rpt-attention__text">
              <span className="rpt-attention__title">{item.title}</span>
              {item.detail && <span className="rpt-attention__detail">{item.detail}</span>}
            </span>
            {item.action && (
              <button type="button" className="rpt-link rpt-attention__action" onClick={item.action.onClick}>
                {item.action.label}
                <LuArrowRight size={13} aria-hidden />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function EnvironmentHealth({ envs }) {
  if (!envs.length) {
    return <ReportEmptyState compact icon={<LuServer size={20} />} title="No environment data" description="No executions were recorded against an environment in this period." />;
  }
  const ordered = [...envs].sort((a, b) => {
    if (!a.totalExecuted !== !b.totalExecuted) return a.totalExecuted ? -1 : 1;
    return a.passRate - b.passRate;
  });
  return (
    <ul className="rpt-envlist">
      {ordered.map((env) => (
        <li key={env.id ?? env.name} className="rpt-envlist__row">
          <span className="rpt-envlist__name" title={env.name}>{env.name}</span>
          {env.totalExecuted > 0 ? (
            <>
              <PassRateBar rate={env.passRate} compact />
              <span className="rpt-envlist__stat">
                <b className={env.failed > 0 ? 'rpt-text--danger' : undefined}>{env.failed}</b> failed
              </span>
              <span className="rpt-envlist__stat rpt-muted">
                {env.totalExecuted}/{env.totalTestCases} run
              </span>
            </>
          ) : (
            <span className="rpt-muted rpt-envlist__none">No runs in this period</span>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function SummaryReport({ ctx, params, update, nav }) {
  const card = useCardData(ctx);
  const envs = useEnvironmentReport(ctx);
  const execSummary = useExecutionSummary(ctx);
  const caseOverview = useCaseOverview(ctx);
  const suites = useSuiteReport(ctx, { page: params.s_page, pageSize: params.s_size, search: params.s_q });
  const history = useExecutionHistory(ctx, { page: params.h_page, pageSize: params.h_size });
  const [suiteSearch, setSuiteSearch] = useDebouncedUrlSearch(params.s_q, (v) => update({ s_q: v }));

  const c = card.data;
  const trendPoints = useMemo(
    () =>
      (c?.trend || []).map((t) => ({
        label: t.label,
        value: t.passRate,
        tooltip: [`${t.passed} passed · ${t.failed} failed · ${t.pending} pending`],
      })),
    [c]
  );
  const ratedPoints = trendPoints.filter((p) => p.value != null);
  const firstRate = ratedPoints[0]?.value;
  const lastRate = ratedPoints[ratedPoints.length - 1]?.value;

  const attention = useMemo(
    () =>
      buildAttentionItems({
        card: c,
        envs: envs.data,
        caseOverview: caseOverview.data,
        execSummary: execSummary.data,
        nav,
      }),
    [c, envs.data, caseOverview.data, execSummary.data, nav]
  );

  if (card.isError && !c) {
    return <ReportErrorState onRetry={() => card.refetch()} />;
  }

  if (c && c.totalExecuted === 0 && c.passed + c.failed + c.pending === 0) {
    return (
      <ReportEmptyState
        icon={<LuPlay size={20} />}
        title="No execution data yet"
        description="Nothing was executed in this period. Run an execution, or widen the date range."
        action={
          <button type="button" className="rt-btn rt-btn--primary rt-btn--sm" onClick={nav.openExecutions}>
            Go to Test Execution
          </button>
        }
      />
    );
  }

  const ta = c?.trendAnalysis;
  const health = execSummary.data?.health;
  const executedPct = c && c.totalTestCases > 0 ? (c.totalExecuted / c.totalTestCases) * 100 : null;
  const passDelta = formatSigned(ta?.passRateTrend, '%');
  const failDelta = formatSigned(ta?.failureRateTrend, '%');
  const timeDelta =
    ta?.executionTimeTrend != null && toNumber(ta.executionTimeTrend) !== 0
      ? `${toNumber(ta.executionTimeTrend) > 0 ? '+' : '−'}${formatDuration(Math.abs(toNumber(ta.executionTimeTrend)))}`
      : null;

  const suiteColumns = [
    {
      key: 'name',
      header: 'Suite',
      sortable: true,
      render: (r) => <span className="rpt-cell-title" title={r.name}>{r.name || '—'}</span>,
    },
    { key: 'testCases', header: 'Tests', sortable: true, align: 'right' },
    { key: 'passed', header: 'Passed', sortable: true, align: 'right', render: (r) => <span className="rpt-text--success">{r.passed}</span> },
    {
      key: 'failed',
      header: 'Failed',
      sortable: true,
      align: 'right',
      render: (r) => <span className={r.failed > 0 ? 'rpt-text--danger rpt-strong' : 'rpt-muted'}>{r.failed}</span>,
    },
    { key: 'pending', header: 'Pending', sortable: true, align: 'right', render: (r) => <span className="rpt-muted">{r.pending}</span> },
    { key: 'passRate', header: 'Pass rate', sortable: true, width: 170, render: (r) => <PassRateBar rate={r.passRate} compact /> },
    { key: 'status', header: 'Status', sortValue: (r) => r.passRate, sortable: true, render: (r) => <HealthPill rate={r.passRate} /> },
    {
      key: 'go',
      header: <span className="rpt-sr-only">Actions</span>,
      align: 'right',
      render: (r) => (
        <button
          type="button"
          className="rpt-link"
          onClick={(e) => {
            e.stopPropagation();
            nav.toReport('detailed', { view: 'runs', r_q: r.name });
          }}
        >
          Runs <LuArrowRight size={13} aria-hidden />
        </button>
      ),
    },
  ];

  const historyColumns = [
    { key: 'date', header: 'Date', render: (r) => formatDate(r.date) },
    { key: 'totalExecuted', header: 'Executed', align: 'right' },
    { key: 'passed', header: 'Passed', align: 'right', render: (r) => <span className="rpt-text--success">{r.passed}</span> },
    { key: 'failed', header: 'Failed', align: 'right', render: (r) => <span className={r.failed > 0 ? 'rpt-text--danger rpt-strong' : 'rpt-muted'}>{r.failed}</span> },
    { key: 'pending', header: 'Pending', align: 'right', render: (r) => <span className="rpt-muted">{r.pending}</span> },
    { key: 'passRate', header: 'Pass rate', width: 170, render: (r) => <PassRateBar rate={r.passRate} compact /> },
    { key: 'avgDuration', header: 'Avg duration', align: 'right', render: (r) => <span className="rpt-mono">{formatDuration(r.avgDuration)}</span> },
  ];

  return (
    <div className="rpt-stack">
      {health?.status && (
        <div className={`rpt-banner rpt-banner--${bannerTone(health.status)}`}>
          <LuGauge size={18} aria-hidden />
          <div className="rpt-banner__text">
            <strong>{health.status}</strong>
            {health.recommendation && <span>{health.recommendation}</span>}
          </div>
          {health.coverage != null && (
            <span className="rpt-banner__meta">Coverage {formatPercent(health.coverage)}</span>
          )}
        </div>
      )}

      {card.isLoading ? (
        <ReportSkeleton variant="kpis" rows={6} />
      ) : (
        <KpiStrip label="Key findings">
          <ReportKpi
            label="Pass rate"
            value={formatPercent(c?.passRate, 1)}
            tone={passRateTone(c?.passRate)}
            delta={passDelta}
            deltaGood={ta?.passRateTrend == null ? null : toNumber(ta.passRateTrend) >= 0}
            hint={passDelta ? 'vs previous period' : undefined}
          />
          <ReportKpi
            label="Executed"
            value={(c?.totalExecuted ?? 0).toLocaleString()}
            hint={c ? `of ${c.totalTestCases.toLocaleString()} test cases` : undefined}
          />
          <ReportKpi label="Passed" value={(c?.passed ?? 0).toLocaleString()} tone="success" />
          <ReportKpi
            label="Failed"
            value={(c?.failed ?? 0).toLocaleString()}
            tone={c?.failed > 0 ? 'danger' : undefined}
            delta={failDelta ? `${failDelta} failure rate` : null}
            deltaGood={ta?.failureRateTrend == null ? null : toNumber(ta.failureRateTrend) <= 0}
          />
          <ReportKpi label="Pending" value={(c?.pending ?? 0).toLocaleString()} tone={c?.pending > 0 ? 'warning' : undefined} />
          <ReportKpi
            label="Execution time"
            value={formatDuration(c?.totalExecutionTime)}
            delta={timeDelta}
            deltaGood={ta?.executionTimeTrend == null ? null : toNumber(ta.executionTimeTrend) <= 0}
            hint={c?.averageExecutionTime != null ? `${formatDuration(c.averageExecutionTime)} avg / test` : undefined}
          />
        </KpiStrip>
      )}

      <div className="rpt-grid rpt-grid--2-3">
        <ReportSection title="Execution outcome" subtitle="How every test case resolved in this period">
          {card.isLoading ? (
            <ReportSkeleton height={120} />
          ) : (
            <OutcomeBar
              segments={[
                { key: 'passed', label: 'Passed', value: c?.passed ?? 0, tone: 'success' },
                { key: 'failed', label: 'Failed', value: c?.failed ?? 0, tone: 'danger' },
                { key: 'pending', label: 'Pending', value: c?.pending ?? 0, tone: 'warning' },
                { key: 'notrun', label: 'Not executed', value: c?.notExecuted ?? 0, tone: 'neutral' },
              ]}
              caption={
                executedPct != null
                  ? `${formatPercent(executedPct, 1)} of test cases were executed at least once.`
                  : undefined
              }
            />
          )}
        </ReportSection>

        <ReportSection
          title="Pass rate trend"
          subtitle={
            ta?.comparisonPeriod?.from
              ? `Previous period: ${formatDate(ta.comparisonPeriod.from)} – ${formatDate(ta.comparisonPeriod.to)}`
              : 'Pass rate for each interval in the selected period'
          }
          actions={
            firstRate != null && lastRate != null && ratedPoints.length > 1 ? (
              <span className="rpt-trend-delta">
                {formatPercent(firstRate)} <LuArrowRight size={12} aria-hidden /> <b>{formatPercent(lastRate)}</b>
                <span className={`rpt-delta ${lastRate >= firstRate ? 'rpt-delta--good' : 'rpt-delta--bad'}`}>
                  {lastRate >= firstRate ? '+' : ''}
                  {(lastRate - firstRate).toFixed(1)} pts
                </span>
              </span>
            ) : null
          }
        >
          {card.isLoading && <ReportSkeleton height={220} />}
          {!card.isLoading && ratedPoints.length === 0 && (
            <ReportEmptyState compact icon={<LuTrendingDown size={20} />} title="No trend yet" description="Pass rate appears once executions are recorded across the period." />
          )}
          {!card.isLoading && ratedPoints.length > 0 && (
            <Suspense fallback={<ReportSkeleton height={220} />}>
              <TrendChart
                points={trendPoints}
                tone="success"
                yMax={100}
                formatValue={(v) => `${Math.round(v)}%`}
                ariaLabel={`Pass rate trend from ${formatPercent(firstRate)} to ${formatPercent(lastRate)}`}
              />
            </Suspense>
          )}
        </ReportSection>
      </div>

      <div className="rpt-grid rpt-grid--3-2">
        <ReportSection title="Needs attention" subtitle="Where to investigate first, ranked by severity">
          {card.isLoading ? <ReportSkeleton variant="list" rows={4} /> : <AttentionList items={attention} />}
        </ReportSection>
        <ReportSection
          title="Environment health"
          subtitle="Weakest environments first"
          actions={
            <button type="button" className="rpt-link" onClick={() => nav.toReport('detailed', { view: 'environments' })}>
              Breakdown <LuArrowRight size={13} aria-hidden />
            </button>
          }
        >
          {envs.isLoading && <ReportSkeleton variant="list" rows={3} />}
          {envs.isError && <ReportErrorState compact onRetry={() => envs.refetch()} />}
          {envs.data && <EnvironmentHealth envs={envs.data} />}
        </ReportSection>
      </div>

      <ReportSection
        title="Test suite health"
        subtitle="Pass rate and failures per suite"
        actions={
          <SearchInput value={suiteSearch} onChange={setSuiteSearch} placeholder="Search suites…" className="rpt-search" />
        }
        flush
      >
        <ReportTable
          caption="Test suite health"
          columns={suiteColumns}
          rows={suites.data?.rows || []}
          rowKey={(r, i) => r.id ?? `${r.name}-${i}`}
          loading={suites.isLoading}
          fetching={suites.isFetching}
          error={suites.isError}
          onRetry={() => suites.refetch()}
          sort={params.s_sort}
          onSortChange={(s) => update({ s_sort: s })}
          rowClassName={(r) => (r.failed > 0 ? 'has-failures' : '')}
          emptyTitle={params.s_q ? 'No suites match your search' : 'No suites executed in this period'}
          pagination={{
            page: params.s_page,
            pageSize: params.s_size,
            total: suites.data?.total || 0,
            onPageChange: (p) => update({ s_page: p }, { push: true }),
            onPageSizeChange: (n) => update({ s_size: n }),
          }}
        />
      </ReportSection>

      <ReportSection
        title="Recent executions"
        subtitle="Daily execution results, newest first"
        actions={
          <button type="button" className="rpt-link" onClick={nav.openExecutions}>
            <LuLayers size={13} aria-hidden /> All executions
          </button>
        }
        flush
      >
        <ReportTable
          caption="Recent executions"
          columns={historyColumns}
          rows={history.data?.rows || []}
          loading={history.isLoading}
          fetching={history.isFetching}
          error={history.isError}
          onRetry={() => history.refetch()}
          rowClassName={(r) => (r.failed > 0 ? 'has-failures' : '')}
          emptyTitle="No executions in this period"
          pagination={{
            page: params.h_page,
            pageSize: params.h_size,
            total: history.data?.total || 0,
            onPageChange: (p) => update({ h_page: p }, { push: true }),
            onPageSizeChange: (n) => update({ h_size: n }),
          }}
        />
      </ReportSection>
    </div>
  );
}

function bannerTone(status) {
  const s = String(status).toLowerCase();
  if (s.includes('healthy') || s.includes('ready') || s.includes('good')) return 'success';
  if (s.includes('risk') || s.includes('critical') || s.includes('fail')) return 'danger';
  if (s.includes('attention') || s.includes('warning')) return 'warning';
  return 'info';
}
